import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import startCase from 'lodash.startcase';
import SectionLoader from '../Loader/SectionLoader';
import BasicModalBody from '../BasicModal/BasicModalBody';
import RowItemElement from '../Buy/RowItem';
import SelectVaultLocation from '../Buy/SelectVaultLocation';
import SelectStorageType from '../Buy/SelectStorageType';
import StandardDepositWizard from '../Modals/Deposit/StandardDepositWizard/StandardDepositWizard';
import IraDepositWizard from '../Modals/DepositWizard/IraDepositWizard';
import MilleniumDepositWizard from '../Modals/DepositWizard/MilleniumDepositWizard';
import QuantityStepper from './QuantityStepper';
import useCheckPermissions from '../../util/hooks/useCheckPermissions';
import { IRA_ENTRUST_TYPE, IRA_EQUITY_TYPE } from '../../util/constants';
import {
  deleteWizardKey,
  setWizardContent,
} from '../../util/helpers/wizardHelpers';
import useProductPrice from '../../util/hooks/useProductPrice';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import { parseEnumType } from '../../util/helpers/enumMappers';
import {
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import {
  ACH_DEPOSIT_WIZARD,
  GTT_TARGET_PRICE,
  GTT_TARGET_QUANTITY,
  IRA_DEPOSIT_WIZARD,
  STANDARD_DEPOSIT_WIZARD,
  TRIGGER_ORDER_WIZARD,
  WIZARD_DATA,
} from '../../constants/sessionStorage';
import { productLocationTypes } from '../../util/enum/api/productTypes';
import {
  getCurrentAccount,
  getCurrentAccountUid,
} from '../../store/selectors/accountSelectors';
import { fetchProductsWithPrices } from '../../store/actions/products/productActions';
import { getPriceForLocation } from '../../store/actions/orders/orderActions';
import { getTriggerOrderQuote } from '../../store/actions/triggerOrders/triggerOrdersActions';
import { GET_TRIGGER_ORDER_QUOTE_LOADING } from '../../store/actions/triggerOrders/triggerOrdersActionConstants';
import { selectLocationPrices } from '../../store/selectors/buyStorageSelectors';
import { selectProductPrices } from '../../store/selectors/productsSelector';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { selectBalancesAvailable } from '../../store/selectors/portfolioBalancesSelectors';
import { selectIsLoadingByActionTypes } from '../../store/selectors/loadingSelectors';
import { fetchPortfolioBalances } from '../../store/actions/portfolio/portfolioActions';
import {
  accountTypeKeys,
  accountTypes,
} from '../../util/enum/api/accountTypes';
import useGtmHook from '../../util/hooks/useGtmHook';
import {
  trackTriggerOrderAddFundsClicked,
  trackTriggerOrderDetailsEntered,
} from './Common/gtmEventHelper';
import {
  AddFundsDimmer,
  ProductHeadingRow,
  ProductName,
  ProductPrice,
  FieldLabel,
  PriceInputWrapper,
  DollarSign,
  PriceInput,
  PriceError,
  PriceAndBelowMarketRow,
  PriceColumn,
  BelowMarketColumn,
  BelowMarketRow,
  BelowMarketButton,
  BelowMarketPrice,
  BelowMarketLabel,
  VaultSelectionWrapper,
  BasicDivider,
  VaultLabelText,
  VaultValue,
  VaultIcon,
  VaultNameText,
  BestPriceBanner,
  SegregatedTag,
  SelectCtaText,
  OrderEntryFooter,
  BuyAtCurrentPriceLink,
  ReviewButtonWrapper,
  ReviewTargetOrderButton,
  QuantityCalcWrapper,
  QuantityCalcTotal,
  QuantityCalcDivider,
  AvailableFundsRow,
  AvailableFundsRequired,
} from './Common';

// Below-market quick-fill pills: exact percentages of the live market price.
const BELOW_MARKET_STEPS = [
  { percent: 5, factor: 0.95 },
  { percent: 10, factor: 0.9 },
];

// GET accounts/{accountUid}/trigger-orders/quotes' `side` param: Buy = -1,
// Sell = 1. Trigger orders only support Buy today.
const TRIGGER_ORDER_SIDE_BUY = -1;

// numeral() (formatMoneyNumeral's underlying formatter) silently returns
// "NaN" for values past roughly 1e21 — well beyond any real product price —
// so cap the integer part far below that instead of trying to format around
// it. 10 digits allows up to $9,999,999,999.99, orders of magnitude more
// than any realistic single-line total.
const MAX_PRICE_INTEGER_DIGITS = 10;

// Keep the price field to at most two decimals and a sane integer length;
// strip anything that isn't a digit or a single decimal point.
const sanitizePrice = (raw) => {
  let value = String(raw).replace(/[^\d.]/g, '');
  const firstDot = value.indexOf('.');
  if (firstDot !== -1) {
    const intPart = value.slice(0, firstDot);
    const decPart = value.slice(firstDot + 1).replace(/\./g, '');
    value = `${intPart.slice(0, MAX_PRICE_INTEGER_DIGITS)}.${decPart.slice(
      0,
      2,
    )}`;
  } else {
    value = value.slice(0, MAX_PRICE_INTEGER_DIGITS);
  }
  return value;
};

// Persists the entered price/quantity into WIZARD_DATA whenever they change, so
// a refresh (which reopens the modal on this step) restores them — matching how
// the vault selection already persists via setWizardData. Rendered inside the
// Formik <Form> so it sees every value source (typed price, below-market pills,
// quantity stepper). Reads live values; writes only once something is entered.
const PersistOrderValues = ({ values, onPersist }) => {
  useEffect(() => {
    if (values.CashAmount === '' && values.QuantityAmount === '') return;
    onPersist({
      CashAmount: values.CashAmount,
      QuantityAmount: values.QuantityAmount,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.CashAmount, values.QuantityAmount]);
  return null;
};

PersistOrderValues.propTypes = {
  values: PropTypes.shape({
    CashAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    QuantityAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  onPersist: PropTypes.func.isRequired,
};

// Second wizard step: set a target price below market, choose a quantity and a
// storage vault, then continue to review (no-op until built) or bail out to the
// regular Buy-at-current-price flow. Modeled on TargetPriceProductSelection for
// the wizard scaffolding and Buy/OrderSelection for the vault + pricing logic.
const TargetPriceOrderEntry = ({
  wizardData,
  setWizardData,
  setWizardTitle,
  setGoBackHandler,
  setCloseHandler,
  handleBack,
  handleNext,
  isLastStep,
  stepIndex,
  isGS,
  onSwitchToBuyFlow,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { ecommerceGtmEvent } = useGtmHook();
  const accountUid = useSelector(getCurrentAccountUid);
  const account = useSelector(getCurrentAccount);
  const locationPrices = useSelector(selectLocationPrices);
  const productPrices = useSelector(selectProductPrices);
  const settings = useSelector(selectSettings);
  const balance = useSelector(selectBalancesAvailable);
  const availableForTrading = balance?.AvailableForTrading;
  const isQuoteLoading = useSelector(
    selectIsLoadingByActionTypes([GET_TRIGGER_ORDER_QUOTE_LOADING]),
  );

  const {
    displayStandardOptionsPermission,
    displayIRAEquityOptionsPermission,
    displayIRAEntrustOptionsPermission,
    displayIRAMilleniumOptionsPermission,
  } = useCheckPermissions();

  const [openSelectVaultLocation, setOpenSelectVaultLocation] = useState(false);
  const [openSelectStorageType, setOpenSelectStorageType] = useState(false);
  const [isVaultPricesLoading, setIsVaultPricesLoading] = useState(false);
  const [isAddFundsOpen, setIsAddFundsOpen] = useState(false);
  // Previous open state, so the session-repair effect below fires only on the
  // close transition rather than on mount.
  const wasAddFundsOpen = useRef(false);

  // The four deposit wizards are permission-gated and mutually exclusive. With
  // none of them available there is nothing to open, so don't hand the user a
  // relabelled button that does nothing when clicked.
  const canAddFunds =
    displayStandardOptionsPermission ||
    displayIRAEquityOptionsPermission ||
    displayIRAEntrustOptionsPermission ||
    displayIRAMilleniumOptionsPermission;

  const selectedPlan = wizardData?.selectedPlan || 'standard';

  const product = wizardData?.selectedProduct;
  const locations = product?.Locations || [];
  const productCode = product?.ProductCode || product?.Code;

  // Instavault (fractional) products have exactly one location and nothing
  // to choose between — mirror ProductCard/Buy's isInstavault check and hide
  // the vault row entirely instead of showing a picker with a single option.
  const isInstavaultProduct =
    isGS &&
    (productCode === settings?.InstavaultProductCodes?.[0] ||
      productCode === settings?.InstavaultProductCodes?.[1]);

  const isIRAAccount =
    parseEnumType(accountTypeKeys, account?.AccountType) === accountTypes.IRA;

  // Default/lowest vault, matching Buy: cheapest non-delivery (LocationType 12)
  // location.
  const minPrice = locations.length
    ? Math.min(...locations.map((l) => l.PricePerUnit))
    : 0;
  const firstLowestVault = locations.find(
    (l) => l.PricePerUnit === minPrice && l.LocationType !== 12,
  );

  // A selectedVault carried over in wizardData from a previously-selected
  // product (e.g. the user went back to product selection and picked a
  // different product) can reference a SymbolCode that doesn't exist on the
  // current product's locations. Confirm it still resolves here — otherwise
  // useProductPrice can't find a matching location and returns undefined,
  // leaving the below-market price pills blank/disabled.
  const isValidVaultSelected = () =>
    !!wizardData?.selectedVault &&
    wizardData.selectedVault.LocationType !== 12 &&
    locations.some(
      (location) => location.SymbolCode === wizardData.selectedVault.SymbolCode,
    );

  const priceLocation = isValidVaultSelected()
    ? wizardData.selectedVault
    : firstLowestVault;
  const livePrice = useProductPrice(product, priceLocation, false, true);

  // Set the header title and route the back chevron to the previous step. Load
  // buy-side product prices so useProductPrice has data even on a
  // session-restored deep entry into this step.
  useEffect(() => {
    setWizardTitle(t('gtt.common.buyAtTargetPrice'));
    setGoBackHandler(() => handleBack);
    return () => setGoBackHandler(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only fetch when prices aren't already loaded. When entering from a product
  // card (or after product selection) prices are already in the store, and
  // re-dispatching flips the global PRODUCT_PRICES_LOADING flag — which the buy
  // listing uses to swap the whole product list (and this modal, when it is
  // hosted by a ProductCard) for a loader, tearing the flow down on open. Fetch
  // only covers a session-restored deep entry with no prices yet.
  useEffect(() => {
    if (accountUid && !productPrices?.length) {
      dispatch(fetchProductsWithPrices({ accountUid, side: 'Buy' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountUid, dispatch]);

  // Available-funds figure shown under the quantity stepper; the GTT flow may be
  // entered directly (deep-link/hand-off) without the balances already loaded.
  useEffect(() => {
    if (accountUid) {
      dispatch(fetchPortfolioBalances({ accountUid }));
    }
  }, [accountUid, dispatch]);

  // Trigger Orders support vault purchases only — delivery (LocationType 12) is
  // not a supported fulfillment option, so exclude it from the vault picker.
  // The default/valid-vault logic already excludes it; this guarantees it never
  // appears as a selectable option either.
  const sortedLocations = [...locationPrices]
    .filter((location) => location.LocationType !== 12)
    .sort((a, b) => a.LocationPrice - b.LocationPrice);

  const fetchVaultLocationPrices = (quantity) => {
    if (!accountUid || !product) return;

    setOpenSelectVaultLocation(true);
    setIsVaultPricesLoading(true);

    dispatch(
      getPriceForLocation({
        accountUid,
        data: {
          productCode: product.ProductCode,
          quantityOrAmount: quantity || 1,
          unitType: 'Quantity',
          paymentMethodType: 'AccountBalance',
        },
        onSuccess: () => setIsVaultPricesLoading(false),
        onError: () => setIsVaultPricesLoading(false),
        isLoading: setIsVaultPricesLoading,
      }),
    );
  };

  const selectedVaultName = () =>
    startCase(
      parseEnumType(
        productLocationTypes,
        wizardData.selectedVault.LocationType,
      ),
    );

  // "Best Price" reflects the vault's standard-storage rate — Segregated adds
  // a storage premium, so the claim no longer holds once it's selected.
  // Mirrors Buy/OrderSelection's vaultTag(), which excludes it the same way.
  const isLowestVaultSelected = () =>
    isValidVaultSelected() &&
    firstLowestVault &&
    wizardData.selectedVault.LocationType === firstLowestVault.LocationType &&
    selectedPlan !== 'premium';

  const isSegregatedVaultSelected = () =>
    isValidVaultSelected() && selectedPlan === 'premium';

  const vaultRowValue = (
    <VaultValue>
      {isValidVaultSelected() && <VaultIcon />}
      <VaultNameText>
        {isValidVaultSelected()
          ? selectedVaultName()
          : t('gtt.triggerOrders.orderEntry.selectVaultPlaceholder')}
      </VaultNameText>
      {isSegregatedVaultSelected() ? (
        <SegregatedTag>
          {settings?.SegregatedCaption || t('buyWizard.premium')}
        </SegregatedTag>
      ) : (
        isLowestVaultSelected() && (
          <BestPriceBanner>{t('buyWizard.bestPrice')}</BestPriceBanner>
        )
      )}
    </VaultValue>
  );

  const validate = (values) => {
    const errors = {};
    if (
      values.CashAmount !== '' &&
      Number(livePrice) > 0 &&
      parseFloat(values.CashAmount) >= Number(livePrice)
    ) {
      errors.CashAmount = t('gtt.errors.priceMustBeLessThanCurrentPrice', {
        livePrice: formatMoneyNumeral(livePrice),
      });
    }
    return errors;
  };

  // The amount that would be reserved for this order (target price × quantity,
  // same figure as the Review screen's totalAtExecution/ReservedFundsBanner)
  // exceeds what's available. Balances load asynchronously, so skip the check
  // (rather than false-flagging as low) until availableForTrading is in.
  const isBalanceLow = (values) => {
    const hasPrice =
      values.CashAmount !== '' && parseFloat(values.CashAmount) > 0;
    const hasQuantity = Number(values.QuantityAmount) >= 1;
    if (!hasPrice || !hasQuantity || availableForTrading === undefined) {
      return false;
    }
    const reservedAmount =
      parseFloat(values.CashAmount) * Number(values.QuantityAmount);
    return reservedAmount > Number(availableForTrading);
  };

  // How much the order overshoots the balance by. Only meaningful once
  // isBalanceLow is true — that's what guarantees the operands are numbers —
  // so every caller reads it from inside a low-balance branch.
  const getShortfall = (values) =>
    parseFloat(values.CashAmount) * Number(values.QuantityAmount) -
    Number(availableForTrading);

  const isReviewDisabled = (values) => {
    const hasPrice =
      values.CashAmount !== '' && parseFloat(values.CashAmount) > 0;
    const hasQuantity = Number(values.QuantityAmount) >= 1;
    // No price or no quantity entered → always disabled, regardless of vault.
    if (!hasPrice || !hasQuantity) return true;
    // Not enough funds to cover the order → keep disabled rather than
    // advancing to Review; the button relabels to "Add Funds" instead.
    if (isBalanceLow(values)) return true;
    const priceBelowMarket =
      Number(livePrice) > 0 &&
      parseFloat(values.CashAmount) < Number(livePrice);
    // Instavault products have no vault picker to satisfy — the single
    // location resolved into priceLocation/firstLowestVault is implicit.
    return !(
      priceBelowMarket &&
      (isValidVaultSelected() || isInstavaultProduct)
    );
  };

  // The deposit wizards report only that they closed, never that a deposit
  // completed, and the balances saga takes no onSuccess — so refetch
  // unconditionally and let isBalanceLow (and with it the button label)
  // re-evaluate against whatever the new balance turns out to be. Same shape
  // as Buy's SelectPaymentOptions after a payment method is added.
  const handleAddFundsClose = () => {
    setIsAddFundsOpen(false);
    if (accountUid) {
      dispatch(fetchPortfolioBalances({ accountUid }));
    }
  };

  // Closing a deposit wizard tears down session state this flow shares with
  // it: Modal clears the whole WIZARD object (avoidClearingStorage is
  // commented out in StandardDepositWizard, so it can't opt out), taking this
  // flow's step index with it, and IraDepositWizard removes WIZARD_DATA
  // regardless of overModal. Neither is visible straight away — GTTFlow holds
  // wizardData in React state — but a refresh afterwards would reopen the flow
  // at product selection with the product gone. Re-persist on the close
  // transition, from an effect so it lands after the wizard's own close
  // handlers instead of being overwritten by them.
  useEffect(() => {
    if (wasAddFundsOpen.current && !isAddFundsOpen) {
      storeInSessionStorage(WIZARD_DATA, wizardData);
      setWizardContent(TRIGGER_ORDER_WIZARD, stepIndex);
      // Drop the deposit wizard's own step keys so a second "Add Funds" click
      // starts at its first screen rather than where the user left off.
      deleteWizardKey(STANDARD_DEPOSIT_WIZARD);
      deleteWizardKey(IRA_DEPOSIT_WIZARD);
      deleteWizardKey(ACH_DEPOSIT_WIZARD);
    }
    wasAddFundsOpen.current = isAddFundsOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddFundsOpen]);

  // The dimmer covers the modal body but not GTT's header, so its close X
  // stays clickable while the deposit wizard is up — and closing the flow
  // there would unmount the deposit wizard from under the user. Point the X at
  // the deposit wizard instead so it closes the top-most layer, and hand it
  // back when that layer is gone.
  useEffect(() => {
    if (!isAddFundsOpen) return undefined;
    setCloseHandler(() => handleAddFundsClose);
    // Hand the X back on close, and on unmount so the flow can never be left
    // pointing at this screen's handler (same shape as setGoBackHandler above).
    return () => setCloseHandler(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddFundsOpen]);

  const handleReview = (values) => {
    // Not enough funds to cover the order: the button reads "Add Funds", so
    // open the deposit wizard over the flow instead of advancing to review.
    // Price/quantity stay put — the wizard is a sibling, the Form never
    // unmounts — and handleAddFundsClose refetches the balance on the way out.
    if (isBalanceLow(values)) {
      if (!canAddFunds) return;
      trackTriggerOrderAddFundsClicked({
        ecommerceGtmEvent,
        productCode: product?.ProductCode || product?.Code,
        targetPrice: values.CashAmount,
        quantity: values.QuantityAmount,
        shortfall: getShortfall(values),
      });
      setIsAddFundsOpen(true);
      return;
    }

    // Fetch a quote for the Review screen (authoritative Subtotal/
    // TargetPriceSavings/TotalAtExecution + the QuoteUid the Review screen's
    // submit executes against). Only advance once the quote comes back.
    dispatch(
      getTriggerOrderQuote({
        accountUid,
        symbolCode: priceLocation?.SymbolCode,
        side: TRIGGER_ORDER_SIDE_BUY,
        quantity: Number(values.QuantityAmount),
        targetPrice: parseFloat(values.CashAmount),
        priceAtSubmission: Number(livePrice),
        isSegregated: selectedPlan === 'premium',
        onSuccess: (quote) => {
          setWizardData({
            orderValues: {
              CashAmount: values.CashAmount,
              QuantityAmount: values.QuantityAmount,
            },
            activeType: 'quantity',
            quote,
            // Carry this screen's live price forward so Review shows the
            // same current price the user saw here, instead of re-fetching
            // (and potentially showing a different) live price.
            productPrice: Number(livePrice),
          });
          trackTriggerOrderDetailsEntered({
            ecommerceGtmEvent,
            productCode: product?.ProductCode || product?.Code,
            targetPrice: values.CashAmount,
            quantity: values.QuantityAmount,
            vault: isValidVaultSelected() ? selectedVaultName() : undefined,
          });
          // Review/Confirmation screens aren't built yet, so don't advance
          // off the last defined step — that would blank the modal (mirrors
          // ProductSelection).
          if (!isLastStep) {
            handleNext();
          }
        },
      }),
    );
  };

  const handleSwitchToBuy = (values) => {
    onSwitchToBuyFlow(
      product,
      wizardData?.selectedVault,
      values.QuantityAmount,
    );
  };

  return (
    <>
      <SectionLoader isLoading={isQuoteLoading}>
        <BasicModalBody>
          <ProductHeadingRow>
            <ProductName>{product?.ShortCaption}</ProductName>
            <ProductPrice>{formatMoneyNumeral(livePrice)}</ProductPrice>
          </ProductHeadingRow>

          <Formik
            initialValues={{
              // Prefer the dedicated GTT_TARGET_PRICE/GTT_TARGET_QUANTITY (they
              // survive a Buy round trip, and a step remount, that can clear
              // orderValues); fall back to orderValues. Re-sanitize on the way
              // in too — a value stored before sanitizePrice existed (or
              // before its integer-length cap was added) would otherwise be
              // restored as-is, bypassing the cap entirely.
              CashAmount: sanitizePrice(
                retrieveFromSessionStorage(GTT_TARGET_PRICE) ||
                  wizardData?.orderValues?.CashAmount ||
                  '',
              ),
              QuantityAmount:
                retrieveFromSessionStorage(GTT_TARGET_QUANTITY) ||
                wizardData?.orderValues?.QuantityAmount ||
                '',
            }}
            enableReinitialize
            validate={validate}
            validateOnBlur={false}
            onSubmit={handleReview}
          >
            {({ values, errors, setFieldValue }) => {
              const balanceLow = isBalanceLow(values);
              return (
                <Form>
                  <PersistOrderValues
                    values={values}
                    onPersist={(orderValues) => {
                      setWizardData({ orderValues });
                      // Mirror price/quantity into their own keys so they survive
                      // a Buy round trip or a step remount that can overwrite or
                      // transiently clear orderValues.
                      storeInSessionStorage(
                        GTT_TARGET_PRICE,
                        orderValues.CashAmount,
                      );
                      storeInSessionStorage(
                        GTT_TARGET_QUANTITY,
                        orderValues.QuantityAmount,
                      );
                    }}
                  />
                  <PriceAndBelowMarketRow>
                    <PriceColumn>
                      <FieldLabel>
                        {t('gtt.triggerOrders.orderEntry.setPrice')}*
                      </FieldLabel>
                      <PriceInputWrapper hasError={!!errors.CashAmount}>
                        <DollarSign isGS={isGS}>$</DollarSign>
                        <PriceInput
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          value={values.CashAmount}
                          onChange={(e) =>
                            setFieldValue(
                              'CashAmount',
                              sanitizePrice(e.target.value),
                            )
                          }
                          data-cy="gtt-target-price"
                        />
                      </PriceInputWrapper>
                      {errors.CashAmount && (
                        <PriceError>{errors.CashAmount}</PriceError>
                      )}
                    </PriceColumn>

                    <BelowMarketColumn>
                      <FieldLabel>
                        {t('gtt.triggerOrders.orderEntry.recommendedSelection')}
                      </FieldLabel>
                      <BelowMarketRow>
                        {BELOW_MARKET_STEPS.map(({ percent, factor }) => {
                          const belowMarketPrice = (
                            Number(livePrice) * factor
                          ).toFixed(2);
                          const isSelected =
                            values.CashAmount !== '' &&
                            values.CashAmount === belowMarketPrice;
                          return (
                            <BelowMarketButton
                              key={percent}
                              type="button"
                              isSelected={isSelected}
                              disabled={!Number(livePrice)}
                              onClick={() =>
                                setFieldValue('CashAmount', belowMarketPrice)
                              }
                              data-cy={`gtt-below-market-${percent}`}
                            >
                              <BelowMarketPrice>
                                {formatMoneyNumeral(Number(livePrice) * factor)}
                              </BelowMarketPrice>
                              <BelowMarketLabel>
                                {t('gtt.triggerOrders.orderEntry.belowMarket', {
                                  percent,
                                })}
                              </BelowMarketLabel>
                            </BelowMarketButton>
                          );
                        })}
                      </BelowMarketRow>
                    </BelowMarketColumn>
                  </PriceAndBelowMarketRow>

                  <FieldLabel style={{ marginTop: 24 }}>
                    {t('gtt.triggerOrders.orderEntry.enterQuantity')}*
                  </FieldLabel>
                  <QuantityStepper
                    value={values.QuantityAmount}
                    onChange={(next) => setFieldValue('QuantityAmount', next)}
                  />

                  {/* Quantity total (at the entered target price) + available
                    funds, mirroring the Buy flow's calculated-values block. */}
                  <QuantityCalcWrapper>
                    {Number(values.QuantityAmount) >= 1 && (
                      <>
                        <QuantityCalcTotal>
                          {Number(values.QuantityAmount)}{' '}
                          {Number(values.QuantityAmount) > 1 ? 'Units' : 'Unit'}{' '}
                          ={' '}
                          <b>
                            {formatMoneyNumeral(
                              Number(values.QuantityAmount) *
                                parseFloat(values.CashAmount || 0),
                            )}
                          </b>
                        </QuantityCalcTotal>
                        <QuantityCalcDivider />
                        <AvailableFundsRow>
                          {t('gtt.triggerOrders.orderEntry.availableFunds')} :{' '}
                          <b>{formatMoneyNumeral(availableForTrading)}</b>
                        </AvailableFundsRow>
                        {balanceLow && (
                          <AvailableFundsRequired data-cy="gtt-amount-required">
                            {t('gtt.triggerOrders.orderEntry.amountRequired', {
                              amount: formatMoneyNumeral(getShortfall(values)),
                            })}
                          </AvailableFundsRequired>
                        )}
                      </>
                    )}
                  </QuantityCalcWrapper>

                  {/* Instavault products have no vault to choose, so the row
                    is hidden — OrderEntryFooter's pinToBottom then takes over
                    the margin-top:auto this row would otherwise carry, so the
                    footer still lands at the bottom instead of floating right
                    under the quantity block. */}
                  {!isInstavaultProduct && (
                    <VaultSelectionWrapper>
                      <BasicDivider />
                      <RowItemElement
                        label={
                          <VaultLabelText>
                            {t('gtt.triggerOrders.orderEntry.vault')}
                          </VaultLabelText>
                        }
                        value={vaultRowValue}
                        clickLabel={
                          <SelectCtaText>
                            {isValidVaultSelected()
                              ? t('buyWizard.changeLabel')
                              : t('buyWizard.selectLabel')}
                          </SelectCtaText>
                        }
                        onClick={() =>
                          fetchVaultLocationPrices(
                            Number(values.QuantityAmount),
                          )
                        }
                        dataCy="gtt-vault-row"
                      />
                    </VaultSelectionWrapper>
                  )}

                  <OrderEntryFooter pinToBottom={isInstavaultProduct}>
                    <BuyAtCurrentPriceLink
                      type="button"
                      onClick={() => handleSwitchToBuy(values)}
                      data-cy="gtt-buy-at-current-price"
                    >
                      <span>
                        {t(
                          'gtt.triggerOrders.orderEntry.buyAtCurrentPriceLine1',
                        )}
                      </span>
                      <span>
                        {t(
                          'gtt.triggerOrders.orderEntry.buyAtCurrentPriceLine2',
                        )}
                      </span>
                    </BuyAtCurrentPriceLink>
                    <ReviewButtonWrapper>
                      <ReviewTargetOrderButton
                        size="lg"
                        variant="primary"
                        type="submit"
                        textTransform="none"
                        fontSize="16px"
                        height={44}
                        disabled={
                          (isReviewDisabled(values) &&
                            !(balanceLow && canAddFunds)) ||
                          isQuoteLoading ||
                          !!errors.CashAmount
                        }
                        data-cy="gtt-review-target-order"
                      >
                        {balanceLow
                          ? t('gtt.triggerOrders.orderEntry.addFunds')
                          : t('gtt.triggerOrders.orderEntry.reviewTargetOrder')}
                      </ReviewTargetOrderButton>
                    </ReviewButtonWrapper>
                  </OrderEntryFooter>
                </Form>
              );
            }}
          </Formik>
        </BasicModalBody>
      </SectionLoader>

      {/* Reuse Buy's vault picker + storage-type drawer for parity. */}
      <SelectVaultLocation
        open={openSelectVaultLocation}
        setOpen={setOpenSelectVaultLocation}
        wizardData={wizardData}
        setWizardData={setWizardData}
        lowestVault={firstLowestVault}
        sortedLocations={sortedLocations}
        locations={locations}
        isLoading={isVaultPricesLoading}
        isIRAAccount={isIRAAccount}
        setOpenSelectStorageType={setOpenSelectStorageType}
        isTargetPriceOrder
      />
      <SelectStorageType
        open={openSelectStorageType}
        setOpen={setOpenSelectStorageType}
        wizardData={wizardData}
        setWizardData={setWizardData}
        setOpenSelectVaultLocation={setOpenSelectVaultLocation}
        selectedPlan={selectedPlan}
        segregatedCaption={settings?.SegregatedCaption}
        isGS={isGS}
      />

      {/* Add Funds, opened from the relabelled footer button when the order
          costs more than the available balance. Rendered outside SectionLoader
          for the same reason as the drawers above: the dimmer is
          position:absolute, and LoaderContainer is a scrolling positioned box,
          so anchoring to the modal card means it covers the whole body.

          overModal is required, not decorative: it keeps StandardDepositWizard
          from clearing this flow's WIZARD_DATA on close, and lifts the wizard's
          portal above GTT's own modal layer. The four branches are
          permission-gated and mutually exclusive, mirroring Buy/OrderSelection. */}
      {isAddFundsOpen && <AddFundsDimmer />}

      {isAddFundsOpen && displayStandardOptionsPermission && (
        <StandardDepositWizard
          isModalOpen={isAddFundsOpen}
          handleClose={handleAddFundsClose}
          overModal
        />
      )}
      {isAddFundsOpen && displayIRAEquityOptionsPermission && (
        <IraDepositWizard
          isModalOpen={isAddFundsOpen}
          handleClose={handleAddFundsClose}
          type={IRA_EQUITY_TYPE}
          overModal
        />
      )}
      {isAddFundsOpen && displayIRAEntrustOptionsPermission && (
        <IraDepositWizard
          isModalOpen={isAddFundsOpen}
          handleClose={handleAddFundsClose}
          type={IRA_ENTRUST_TYPE}
          overModal
        />
      )}
      {isAddFundsOpen && displayIRAMilleniumOptionsPermission && (
        <MilleniumDepositWizard
          isModalOpen={isAddFundsOpen}
          handleClose={handleAddFundsClose}
          overModal
        />
      )}
    </>
  );
};

TargetPriceOrderEntry.propTypes = {
  wizardData: PropTypes.shape({
    selectedProduct: PropTypes.shape({
      ProductCode: PropTypes.string,
      Code: PropTypes.string,
      Caption: PropTypes.string,
      Locations: PropTypes.arrayOf(
        PropTypes.shape({
          SymbolCode: PropTypes.string,
          LocationType: PropTypes.number,
          PricePerUnit: PropTypes.number,
        }),
      ),
    }),
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
      SymbolCode: PropTypes.string,
    }),
    orderValues: PropTypes.shape({
      CashAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      QuantityAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    selectedPlan: PropTypes.string,
  }),
  setWizardData: PropTypes.func,
  setWizardTitle: PropTypes.func,
  setGoBackHandler: PropTypes.func,
  // Overrides the modal's close X while the deposit wizard is open, so it
  // closes that layer instead of tearing down the whole flow.
  setCloseHandler: PropTypes.func,
  handleBack: PropTypes.func,
  handleNext: PropTypes.func,
  isLastStep: PropTypes.bool,
  // This screen's index in the wizard, re-persisted after a deposit wizard
  // clears the shared WIZARD session object on close.
  stepIndex: PropTypes.number,
  isGS: PropTypes.bool,
  onSwitchToBuyFlow: PropTypes.func,
};

export default TargetPriceOrderEntry;
